import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, Download, Copy, Building2, Flame, Users, ChevronDown, ChevronRight, CalendarPlus, CheckSquare, Square } from 'lucide-react';
import { useAuth } from '../App';
import TrainingScheduleModal, { fmtRange } from './TrainingScheduleModal';
import TrainingSessionsList from './TrainingSessionsList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const esc = (t) => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const STATUS_TXT = { expired: 'Expired', expiring: 'Expiring', missing: 'Never had one' };
const DIVISIONS = [['field', 'Field'], ['shop', 'Shop'], ['office', 'Office']];
const byDivision = (rows) => DIVISIONS.map(([id, label]) => [id, label, rows.filter((r) => (r.division || 'office') === id)]).filter((x) => x[2].length);
const STATUS_CLS = {
  expired: 'bg-red-50 text-red-700 border-red-200',
  expiring: 'bg-amber-50 text-amber-700 border-amber-200',
  missing: 'bg-slate-200 text-slate-700 border-slate-500/30',
};

// Printable booking sheet for one trainer: roster by course + who needs what, so it can go straight to Chad / CWB / St. John.
export const printTrainingReport = (p, days) => {
  const w = window.open('', '_blank');
  if (!w) { toast.error('Pop-up blocked — allow pop-ups to print'); return; }
  const today = new Date().toLocaleDateString('en-CA');
  let h = `<html><head><title>Training needed — ${esc(p.name)}</title><style>
    body{font-family:Arial,sans-serif;font-size:12px;color:#000;margin:24px}
    h1{font-size:18px;margin:0 0 2px 0} h2{font-size:14px;margin:18px 0 6px 0;border-bottom:2px solid #000;padding-bottom:2px}
    .meta{color:#444;font-size:11px;margin-bottom:12px} table{width:100%;border-collapse:collapse;margin-bottom:8px}
    th,td{border:1px solid #999;padding:4px 6px;text-align:left;vertical-align:top} th{background:#eee}
    .exp{color:#b00020;font-weight:bold} .soon{color:#8a5a00;font-weight:bold} .miss{color:#555}
    .sum{display:flex;gap:18px;margin:8px 0 14px 0;font-size:12px} .sum b{font-size:16px}
    .att{page-break-before:always} .att h2{font-size:20px;border-bottom:3px solid #000;margin-top:0}
    .att h3{font-size:14px;text-transform:uppercase;letter-spacing:.5px;margin:16px 0 6px 0;color:#222}
    .att ul{list-style:none;padding:0;margin:0;columns:2;column-gap:32px} .att li{font-size:16px;line-height:1.55;break-inside:avoid;border-bottom:1px dotted #bbb;padding:3px 0}
    .att li .box{display:inline-block;width:14px;height:14px;border:2px solid #000;vertical-align:-2px;margin-right:8px}
    .att li small{font-size:11px;color:#555;margin-left:6px}
    @media print{ .nop{display:none} }
  </style></head><body>`;
  h += `<h1>Training needed — ${esc(p.name)}${p.contact ? ` <span style="font-size:13px;font-weight:normal">(${esc(p.contact)})</span>` : ''}</h1>`;
  h += `<div class="meta">365 Industrial Services Inc. · prepared ${today} · tickets expired or expiring within ${days} days${p.note ? ` · ${esc(p.note)}` : ''}</div>`;
  const pbd = p.people_by_division || {};
  h += `<div class="sum"><div><b>${p.total_people}</b> people <span style="color:#555">(${pbd.field || 0} field · ${pbd.shop || 0} shop · ${pbd.office || 0} office)</span></div><div><b>${p.total_seats}</b> seats</div><div class="exp"><b>${p.expired}</b> expired</div><div class="soon"><b>${p.expiring}</b> expiring</div>${p.missing ? `<div class="miss"><b>${p.missing}</b> never had one</div>` : ''}</div>`;
  h += `<h2>By course</h2>`;
  p.courses.forEach((c) => {
    h += `<table><thead><tr><th colspan="4">${esc(c.label)} — ${c.employees.length} ${c.employees.length === 1 ? 'person' : 'people'}${c.by_division ? ` <span style="font-weight:normal">(${c.by_division.field || 0} field · ${c.by_division.shop || 0} shop${c.by_division.office ? ` · ${c.by_division.office} office` : ''})</span>` : ''}</th></tr>
      <tr><th style="width:32%">Employee</th><th style="width:18%">Role</th><th style="width:20%">Status</th><th>Current ticket / expiry</th></tr></thead><tbody>`;
    byDivision(c.employees).forEach(([, label, rows]) => {
      h += `<tr><td colspan="4" style="background:#f4f4f4;font-weight:bold;text-transform:uppercase;font-size:10px;letter-spacing:.5px">${label} — ${rows.length}</td></tr>`;
      rows.forEach((e) => {
        const cls = e.status === 'expired' ? 'exp' : e.status === 'expiring' ? 'soon' : 'miss';
        h += `<tr><td>${esc(e.name)}</td><td>${esc((e.role || '').replace(/_/g, ' '))}</td><td class="${cls}">${STATUS_TXT[e.status] || e.status}${e.expiry ? ` ${e.expiry}` : ''}</td><td>${esc(e.ticket || '—')}</td></tr>`;
      });
    });
    h += `</tbody></table>`;
  });
  h += `<h2>By person</h2>`;
  byDivision(p.people).forEach(([, label, rows]) => {
    h += `<table><thead><tr><th colspan="2">${label} — ${rows.length} ${rows.length === 1 ? 'person' : 'people'}</th></tr><tr><th style="width:32%">Employee</th><th>Needs</th></tr></thead><tbody>`;
    rows.forEach((pp) => {
      h += `<tr><td>${esc(pp.name)}</td><td>${pp.courses.map((c) => `<span class="${c.status === 'expired' ? 'exp' : c.status === 'expiring' ? 'soon' : 'miss'}">${esc(c.label)}</span>${c.expiry ? ` (${c.expiry})` : ''}`).join(' · ')}</td></tr>`;
    });
    h += `</tbody></table>`;
  });
  // Attendance list — the page you hand to the trainer / pin up: big names, tick boxes, grouped shop / field
  const SHORT = { 'Worker Health & Safety Awareness': 'Worker Awareness', 'Supervisor Health & Safety Awareness': 'Supervisor Awareness', 'Working at Heights / Fall Protection': 'WAH', 'Lockout / Tagout': 'LOTO', 'Lift Truck / Forklift': 'Lift Truck', 'Overhead Crane & Hoist': 'Crane', 'Mobile Crane (0–8 ton)': 'Mobile Crane', 'WHMIS / GHS': 'WHMIS', 'Propane Handling': 'Propane', 'Rigging & Hoisting Safe Practices': 'Rigging', 'Confined Space Entry': 'Confined Space', 'First Aid / CPR': 'First Aid', 'Elevated Work Platforms (Scissor / PEWP / AWP)': 'EWP / Scissor' };
  h += `<div class="att"><h2>Attendance list — ${esc(p.name)}${p.contact ? ` (${esc(p.contact)})` : ''}</h2>
    <p class="meta">${p.total_people} ${p.total_people === 1 ? 'person' : 'people'} to attend · expired or expiring within ${days} days · tick as they arrive</p>`;
  byDivision(p.people).forEach(([, label, rows]) => {
    h += `<h3>${label} — ${rows.length}</h3><ul>`;
    rows.forEach((pp) => {
      h += `<li><span class="box"></span>${esc(pp.name)}${pp.no_tickets_on_file ? ' <span class="miss" style="font-size:11px;font-weight:bold">(no tickets on file)</span>' : ''}<small>${pp.courses.map((c) => esc(SHORT[c.label] || c.label)).join(', ')}</small></li>`;
    });
    h += `</ul>`;
  });
  h += `</div>`;
  h += `<p class="meta">Generated from 365Ops safety tickets — the newest card in each employee's folder decides. Shop / field is by role (millwrights = field; welders, fitters, shop foreman, labourers = shop).</p>`;
  h += `<script>setTimeout(function(){window.print();},300);</script></body></html>`;
  w.document.open(); w.document.write(h); w.document.close();
};

const csvFor = (p) => {
  const q = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [['Trainer', 'Course', 'Shop / Field', 'Employee', 'Role', 'Status', 'Expiry', 'Ticket on file']];
  p.courses.forEach((c) => c.employees.forEach((e) => rows.push([p.name, c.label, e.division || '', e.name, e.role || '', STATUS_TXT[e.status] || e.status, e.expiry || '', e.ticket || ''])));
  return rows.map((r) => r.map(q).join(',')).join('\n');
};

const ProviderCard = ({ p, active, onClick }) => (
  <button onClick={onClick} data-testid={`tr-provider-${p.id}`}
    className={`text-left rounded-xl border p-4 transition-colors ${active ? 'bg-blue-50 border-blue-500' : 'bg-white border-slate-200 hover:border-slate-500'}`}>
    <div className="flex items-center gap-2 mb-1">
      {p.kind === 'internal' ? <Flame className="w-4 h-4 text-orange-700 shrink-0" /> : <Building2 className="w-4 h-4 text-blue-700 shrink-0" />}
      <span className="text-slate-900 font-semibold text-sm leading-tight">{p.name}</span>
    </div>
    {p.contact && <p className="text-xs text-slate-500">{p.contact}</p>}
    <p className="text-xs text-slate-500 mt-1 truncate" title={(p.topics || []).join(', ')}>{p.courses.length} course{p.courses.length !== 1 ? 's' : ''} · {p.total_people} {p.total_people === 1 ? 'person' : 'people'}</p>
    <div className="flex flex-wrap gap-1 mt-2">
      {p.expired > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">{p.expired} expired</span>}
      {p.expiring > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{p.expiring} expiring</span>}
      {p.missing > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 border border-slate-500/30">{p.missing} never had</span>}
      {p.total_seats === 0 && <span className="text-[10px] text-emerald-700">Nothing due</span>}
    </div>
  </button>
);

const RosterCourse = ({ c }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white rounded-xl border border-slate-200" data-testid={`tr-course-${c.topic}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 p-3 text-left">
        <span className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />}
          <span className="text-slate-900 font-semibold truncate">{c.label}</span>
          <span className="text-xs text-slate-500 shrink-0">{c.employees.length}</span>
        </span>
        <span className="flex gap-1 shrink-0">
          {c.expired > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700">{c.expired} expired</span>}
          {c.expiring > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700">{c.expiring} expiring</span>}
          {c.missing > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700">{c.missing} never had</span>}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {byDivision(c.employees).map(([id, label, rows]) => (
            <div key={id} data-testid={`tr-div-${c.topic}-${id}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label} · {rows.length}</p>
              <div className="grid gap-1 sm:grid-cols-2">
                {rows.map((e) => (
                  <div key={e.employee_id} className="flex items-center justify-between gap-2 text-sm px-2.5 py-1 rounded-lg bg-slate-50" data-testid={`tr-row-${c.topic}-${e.employee_id}`}>
                    <span className="text-slate-800 truncate">{e.name}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_CLS[e.status]}`}>{STATUS_TXT[e.status]}{e.expiry ? ` ${e.expiry}` : ''}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Safety & Training → Mass Training → "By trainer": one booking roster per provider, printable. super_admin / admin / HR
// can tick people and book a session (chat message + dashboard card + schedule block).
export default function TrainingReports({ days, includeStale }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState('cm_safety');
  const [booking, setBooking] = useState({ sessions: [], booked: {}, default_days: {}, can_schedule: false });
  const [picked, setPicked] = useState(new Set());
  const [showModal, setShowModal] = useState(false);

  const loadSessions = useCallback(() => {
    axios.get(`${API}/training-sessions`).then((r) => setBooking(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { setPicked(new Set()); }, [sel]);

  useEffect(() => {
    let on = true;
    axios.get(`${API}/certificates/training-report`, { params: { days, stale_years: includeStale ? 0 : 3 } })
      .then((r) => { if (on) setData(r.data); })
      .catch(() => toast.error('Failed to load trainer reports'));
    return () => { on = false; };
  }, [days, includeStale]);

  if (!data) return <p className="text-slate-500 text-center py-8">Loading…</p>;
  const p = data.providers.find((x) => x.id === sel) || data.providers[0];
  const canSchedule = booking.can_schedule && ['super_admin', 'admin', 'hr'].includes(user?.role);
  const providerSessions = booking.sessions.filter((s) => s.provider_id === p.id);
  const bookedHere = (eid) => (booking.booked[eid] || []).find((b) => b.provider_id === p.id);
  const bookedElsewhere = (eid) => (booking.booked[eid] || []).filter((b) => b.provider_id !== p.id);
  const toggle = (eid) => setPicked((s) => { const n = new Set(s); n.has(eid) ? n.delete(eid) : n.add(eid); return n; });
  const toggleMany = (rows) => setPicked((s) => {
    const n = new Set(s);
    const free = rows.filter((pp) => !bookedHere(pp.employee_id)).map((pp) => pp.employee_id);
    const allIn = free.every((id) => n.has(id));
    free.forEach((id) => allIn ? n.delete(id) : n.add(id));
    return n;
  });
  const pickedRows = p.people.filter((pp) => picked.has(pp.employee_id));
  const copyNames = () => {
    navigator.clipboard.writeText(p.people.map((pp) => `${pp.name} — ${pp.courses.map((c) => c.label).join(', ')}`).join('\n'));
    toast.success(`Copied ${p.people.length} names for ${p.name}`);
  };
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csvFor(p)], { type: 'text/csv' }));
    a.download = `training-${p.id}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="training-reports">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-testid="tr-providers">
        {data.providers.map((x) => <ProviderCard key={x.id} p={x} active={x.id === p.id} onClick={() => setSel(x.id)} />)}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="tr-report">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <h3 className="text-slate-900 font-bold text-base md:text-lg" data-testid="tr-report-title">{p.name}{p.contact ? <span className="text-slate-500 font-normal text-sm"> · {p.contact}</span> : null}</h3>
            <p className="text-xs text-slate-500 mt-0.5" data-testid="tr-report-counts">
              {p.total_people} {p.total_people === 1 ? 'person' : 'people'} ({p.people_by_division?.field || 0} field · {p.people_by_division?.shop || 0} shop{p.people_by_division?.office ? ` · ${p.people_by_division.office} office` : ''}) · {p.total_seats} seat{p.total_seats !== 1 ? 's' : ''} across {p.courses.length} course{p.courses.length !== 1 ? 's' : ''}
              {p.note ? ` · ${p.note}` : ''}
              {p.id === 'other' && ' · Tell me who runs these and I\u2019ll move them under that trainer.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => printTrainingReport(p, data.days)} disabled={!p.total_seats} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white flex items-center gap-1" data-testid="tr-print"><Printer className="w-3.5 h-3.5" /> Print report</button>
            <button onClick={download} disabled={!p.total_seats} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-800 hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1" data-testid="tr-csv"><Download className="w-3.5 h-3.5" /> CSV</button>
            <button onClick={copyNames} disabled={!p.total_seats} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-800 hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1" data-testid="tr-copy"><Copy className="w-3.5 h-3.5" /> Copy names</button>
            {canSchedule && p.id !== 'other' && (
              <button onClick={() => setShowModal(true)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-purple-600 hover:bg-purple-500 text-white flex items-center gap-1" title="Book a session — tick people below first, or add anyone inside" data-testid="tr-book-session"><CalendarPlus className="w-3.5 h-3.5" /> {picked.size ? `Schedule training (${picked.size})` : 'Book a session'}</button>
            )}
          </div>
        </div>
        {p.total_seats === 0 ? (
          <p className="text-emerald-700 text-sm py-6 text-center" data-testid="tr-empty">Nothing due for {p.name} within {data.days} days.</p>
        ) : (
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="space-y-2">
              <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">By course</p>
              {p.courses.map((c) => <RosterCourse key={c.topic} c={c} />)}
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1"><Users className="w-3.5 h-3.5" /> By person</p>
                {canSchedule && (
                  <button onClick={() => toggleMany(p.people)} className="text-[11px] text-purple-700 hover:text-slate-900 flex items-center gap-1" data-testid="tr-pick-all">
                    <CheckSquare className="w-3.5 h-3.5" /> {p.people.filter((pp) => !bookedHere(pp.employee_id)).every((pp) => picked.has(pp.employee_id)) ? 'Clear all' : 'Tick everyone'}
                  </button>
                )}
              </div>
              {canSchedule && <p className="text-[11px] text-slate-500 -mt-2">Tick who's going to the next session, then “Schedule training” at the bottom.</p>}
              {byDivision(p.people).map(([id, label, rows]) => (
                <div key={id} className="space-y-1" data-testid={`tr-people-${id}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label} · {rows.length}</p>
                    {canSchedule && <button onClick={() => toggleMany(rows)} className="text-[10px] text-purple-700 hover:text-slate-900" data-testid={`tr-pick-all-${id}`}>{rows.filter((pp) => !bookedHere(pp.employee_id)).every((pp) => picked.has(pp.employee_id)) ? 'clear' : `tick all ${label.toLowerCase()}`}</button>}
                  </div>
                  {rows.map((pp) => {
                    const here = bookedHere(pp.employee_id);
                    const other = bookedElsewhere(pp.employee_id);
                    const on = picked.has(pp.employee_id);
                    return (
                      <div key={pp.employee_id} className={`flex flex-wrap items-center gap-2 text-sm px-3 py-1.5 rounded-lg border ${on ? 'bg-purple-50 border-purple-300' : 'bg-white border-slate-200'}`} data-testid={`tr-person-${pp.employee_id}`}>
                        {canSchedule && (
                          <button onClick={() => !here && toggle(pp.employee_id)} disabled={!!here} className={`shrink-0 ${here ? 'text-slate-600 cursor-not-allowed' : on ? 'text-purple-700' : 'text-slate-500 hover:text-slate-900'}`} title={here ? `Already booked ${here.label}` : 'Tick for the next session'} data-testid={`tr-pick-${pp.employee_id}`} aria-pressed={on}>
                            {on ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                          </button>
                        )}
                        <span className="text-slate-900 font-medium min-w-[150px]">{pp.name}{pp.no_tickets_on_file && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 font-bold">NO TICKETS ON FILE</span>}</span>
                        {here && <span className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-800 border border-purple-200 font-semibold" data-testid={`tr-booked-${pp.employee_id}`}>📅 Booked {here.label}</span>}
                        {other.map((b) => <span key={b.session_id} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-300" title={`Also booked with ${b.provider_name}`}>📅 {b.provider_name} {b.label}</span>)}
                        <span className="flex flex-wrap gap-1">
                          {pp.courses.map((c) => <span key={c.topic} className={`text-[11px] px-2 py-0.5 rounded-full border ${STATUS_CLS[c.status]}`} title={`${STATUS_TXT[c.status]}${c.expiry ? ` ${c.expiry}` : ''}`}>{c.label}</span>)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <TrainingSessionsList sessions={providerSessions} canSchedule={canSchedule} onChanged={loadSessions} />

      {canSchedule && picked.size > 0 && (
        <div className="sticky bottom-3 z-30 flex flex-wrap items-center justify-between gap-2 bg-purple-100 border border-purple-300 rounded-xl px-4 py-3 shadow-2xl backdrop-blur" data-testid="training-select-bar">
          <p className="text-sm text-purple-800"><b>{picked.size}</b> {picked.size === 1 ? 'person' : 'people'} ticked for {p.name}{providerSessions.length ? ` · next session ${fmtRange(providerSessions[0].start_date, providerSessions[0].end_date)}` : ''}</p>
          <div className="flex gap-2">
            <button onClick={() => setPicked(new Set())} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-300 text-slate-800 hover:bg-slate-100" data-testid="training-select-clear">Clear</button>
            <button onClick={() => setShowModal(true)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white flex items-center gap-1" data-testid="training-schedule-btn"><CalendarPlus className="w-3.5 h-3.5" /> Schedule training</button>
          </div>
        </div>
      )}
      {showModal && (
        <TrainingScheduleModal provider={p} people={pickedRows} defaultDays={booking.default_days?.[p.id] || 1} existing={providerSessions}
          onClose={() => setShowModal(false)} onDone={() => { setShowModal(false); setPicked(new Set()); loadSessions(); }} />
      )}
    </div>
  );
}
